/**
 * Platform Admin / Control Plane (B-34 F4) — API staff-only.
 * Tenant recebe 404 do backend; usePlatformAccess usa isso para esconder a área.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/services/api';
import { useAuth } from '@/store/useAuth';

export interface PlatformClient {
  id: string;
  slug: string;
  name: string;
  plan: 'standalone' | 'enterprise';
  ai_mode: 'pool' | 'own';
  status: 'active' | 'trialing' | 'past_due' | 'suspended' | 'deleting' | 'churned';
  package: string | null;
  host: string | null;
  provisioning_status: string;
  features: Record<string, boolean>;
  quotas: Record<string, number>;
  brands: string[];
  users: number;
  spend_month_usd: number;
  created_at: string | null;
}

export interface FeatureDef {
  key: string;
  label: string;
}

export interface PackageDef {
  key: string;
  label: string;
  plan: 'standalone' | 'enterprise';
  features: Record<string, boolean>;
  quotas: Record<string, number>;
  trial_days: number | null;
  /** Mensalidade de face, NA MOEDA de `moeda` — nunca assuma real. */
  price_month: number;
  moeda: string;
  /** Aparece na vitrine pública? Pacote antigo continua no catálogo e fora dela. */
  publico: boolean;
}

export interface PackageCatalog {
  features: FeatureDef[];
  quota_dims: string[];
  packages: PackageDef[];
  default: string;
}

export interface BillingItem {
  client_id: string | null;
  client_slug: string;
  client_name: string;
  provider: string;
  spent_usd: number;
}

export interface BillingOut {
  period: string;
  total_usd: number;
  items: BillingItem[];
}

export interface ClientCreatePayload {
  slug: string;
  name: string;
  package?: string;
  plan: string;
  ai_mode: string;
  features?: Record<string, boolean>;
  admin_email?: string;
  admin_nome?: string;
}

export interface ClientCreateOut {
  id: string;
  slug: string;
  package?: string | null;
  admin_email?: string;
  temp_password?: string;
}

const KEY = ['platform', 'clients'];

export function usePlatformClients(enabled = true) {
  return useQuery({
    queryKey: KEY,
    // O 404 aqui e RESPOSTA ESPERADA, nao incidente: o backend responde 404 a
    // qualquer tenant de proposito (nao vaza a existencia do plano de controle)
    // e esta sonda roda em TODA sessao para decidir se o menu "Plataforma"
    // aparece. Sem o `esperados`, cada admin de tenant abria o produto e levava
    // um toast vermelho "Recurso nao encontrado" no primeiro segundo — medido
    // ao vivo com uma conta recem-provisionada.
    queryFn: () => api.get<{ clients: PlatformClient[] }>('/platform/clients', { esperados: [404] }),
    select: (d) => d.clients,
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/** Sonda de acesso: só admins tentam; 404 = não é staff → área invisível. */
export function usePlatformAccess() {
  const role = useAuth((s) => s.role);
  const q = usePlatformClients(role === 'admin');
  return { isStaff: q.isSuccess, clients: q.data ?? [], query: q };
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientCreatePayload) =>
      api.post<ClientCreateOut>('/platform/clients', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<PlatformClient> }) =>
      api.patch<PlatformClient>(`/platform/clients/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, site_slug }: { id: string; site_slug: string }) =>
      api.post<{ site: string; client: string }>(`/platform/clients/${id}/sites`, { site_slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePlatformBilling(month?: string) {
  return useQuery({
    queryKey: ['platform', 'billing', month ?? 'current'],
    queryFn: () =>
      api.get<BillingOut>(`/platform/billing${month ? `?month=${month}` : ''}`),
    retry: false,
    staleTime: 30_000,
  });
}

// ─── Cockpit: overview + analytics cruzado + consumo ─────────────────────────

export interface PlatformOverview {
  clients: { total: number; enterprise: number; standalone: number; active: number; suspended: number };
  brands: number;
  users: { total: number; staff: number; tenant: number };
  content: { pautas: number; publicadas: number };
  spend: { month_usd: number; total_usd: number };
  mrr_brl: number;
}

export interface AnalyticsClient {
  client_slug: string;
  client_name: string;
  articles: number;
  pageviews: number;
  clicks: number;
  impressions: number;
  ctr: number;
}

export interface PlatformAnalytics {
  window: string;
  totals: { pageviews: number; clicks: number; impressions: number };
  by_client: AnalyticsClient[];
}

export interface UsageClient {
  client_slug: string;
  client_name: string;
  points: Record<string, number>;
}

export interface PlatformUsage {
  since: string;
  by_client: UsageClient[];
}

export function usePlatformOverview() {
  return useQuery({
    queryKey: ['platform', 'overview'],
    queryFn: () => api.get<PlatformOverview>('/platform/overview'),
    retry: false,
    staleTime: 30_000,
  });
}

export function usePlatformAnalytics(window = '28d') {
  return useQuery({
    queryKey: ['platform', 'analytics', window],
    queryFn: () => api.get<PlatformAnalytics>(`/platform/analytics?window=${window}`),
    retry: false,
    staleTime: 30_000,
  });
}

export function usePlatformUsage(months = 6) {
  return useQuery({
    queryKey: ['platform', 'usage', months],
    queryFn: () => api.get<PlatformUsage>(`/platform/usage?months=${months}`),
    retry: false,
    staleTime: 30_000,
  });
}

export function usePackages() {
  return useQuery({
    queryKey: ['platform', 'packages'],
    queryFn: () => api.get<PackageCatalog>('/platform/packages'),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface SerpGlobal {
  active_provider: string;
  providers: string[];
  key: { present: boolean; source: string | null; masked: string | null };
  consumption: { provider: string; spent_usd: number }[];
  note: string;
}

export interface VigiasGlobal {
  by_client: {
    client_slug: string;
    client_name: string;
    vigias: { slug: string; nome: string }[];
    events: number;
  }[];
}

// B-47: uso real por área de feature, minerado do audit_log (staff).
export interface FeatureUsageReport {
  window_days: number;
  generated_at: string;
  used: {
    area: string;
    label: string;
    events: number;
    actors: number;
    clients: number;
    last_event_at: string | null;
    actions: { action: string; events: number }[];
  }[];
  silent_areas: { area: string; label: string }[];
}

export function useFeatureUsage(days: number) {
  return useQuery({
    queryKey: ['platform', 'feature-usage', days],
    queryFn: () => api.get<FeatureUsageReport>(`/platform/feature-usage?days=${days}`),
    retry: false,
    staleTime: 60_000,
  });
}

export function usePlatformSerp() {
  return useQuery({
    queryKey: ['platform', 'serp'],
    queryFn: () => api.get<SerpGlobal>('/platform/serp'),
    retry: false,
    staleTime: 30_000,
  });
}

export function usePlatformVigias() {
  return useQuery({
    queryKey: ['platform', 'vigias-global'],
    queryFn: () => api.get<VigiasGlobal>('/platform/vigias'),
    retry: false,
    staleTime: 30_000,
  });
}

export function useAccessInfo() {
  return useQuery({
    queryKey: ['platform', 'access-info'],
    queryFn: () => api.get<{ app_url: string | null }>('/platform/access-info'),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface ResetAdminOut {
  admin_email: string;
  temp_password: string;
  slug: string;
}

export function useExportClient() {
  return useMutation({
    mutationFn: (id: string) => api.get<Record<string, unknown>>(`/platform/clients/${id}/export`),
  });
}

export function useDeprovisionClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirm_slug, mode }: { id: string; confirm_slug: string; mode: 'suspend' | 'soft_delete' }) =>
      api.post<{ slug: string; status: string }>(`/platform/clients/${id}/deprovision`, { confirm_slug, mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'clients'] }),
  });
}

export function usePurgeClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirm_slug }: { id: string; confirm_slug: string }) =>
      api.post<{ purged: string }>(`/platform/clients/${id}/purge`, { confirm_slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'clients'] }),
  });
}

export function useResetAdmin() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<ResetAdminOut>(`/platform/clients/${id}/reset-admin`, {}),
  });
}
